if (!customElements.get('rone-flavor-selector')) {
  customElements.define(
    'rone-flavor-selector',
    class RoneFlavorSelector extends HTMLElement {
      connectedCallback() {
        this.msrp = parseFloat(this.dataset.msrp || '0');
        this.priceLadder = JSON.parse(this.dataset.priceLadder || '{}');
        this.renewalPriceLadder = JSON.parse(this.dataset.renewalPriceLadder || '{}');
        this.cadenceLadder = JSON.parse(this.dataset.cadenceLadder || '{}');
        this.sellingPlans = JSON.parse(this.dataset.sellingPlans || '{}');
        this.discountCode = this.dataset.discountCode || '';

        this.rows = Array.from(this.querySelectorAll('[data-flavor-row]'));
        this.titleEl = this.querySelector('[data-flavor-title]');
        this.sumRow = this.querySelector('[data-sum-row]');
        this.savePct = this.querySelector('[data-save-pct]');
        this.bagCount = this.querySelector('[data-bag-count]');
        this.cta = this.querySelector('[data-cta-link]');
        this.ctaLabel = this.querySelector('[data-cta-label]');
        this.ctaPriceWrap = this.querySelector('[data-cta-price]');
        this.ctaMsrp = this.querySelector('[data-cta-msrp]');
        this.ctaTotal = this.querySelector('[data-cta-total]');
        this.cadLine = this.querySelector('[data-cad-line]');
        this.giftItems = Array.from(this.querySelectorAll('[data-gift-item]'));
        this.giftCount = this.querySelector('[data-gift-unlocked-count]');

        this.rows.forEach((row) => {
          const variantId = row.dataset.variantId;
          row.querySelector('[data-flavor-add]').addEventListener('click', () => this.adjust(variantId, 1));
          row.querySelector('[data-flavor-inc]').addEventListener('click', () => this.adjust(variantId, 1));
          row.querySelector('[data-flavor-dec]').addEventListener('click', () => this.adjust(variantId, -1));
        });

        if (this.cta) {
          this.cta.addEventListener('click', (event) => {
            event.preventDefault();
            if (this.cta.classList.contains('rone-cta--disabled')) return;
            this.addToCart();
          });
        }

        // All three funnel stages render on one template; re-read the query string and
        // start fresh with an empty selection each time the shopper (re)enters this stage.
        this.handleStageEnter = (event) => {
          if (event.detail.stage === 'flavors') this.refreshFromQuery();
        };
        document.addEventListener('rone:stage-enter', this.handleStageEnter);

        this.refreshFromQuery();
      }

      disconnectedCallback() {
        document.removeEventListener('rone:stage-enter', this.handleStageEnter);
      }
      clearCart() {
        return fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }).catch(function () {});
      }

      refreshFromQuery() {
        const params = new URLSearchParams(window.location.search);
        this.minBags = Math.min(Math.max(parseInt(params.get('quantity'), 10) || 1, 1), 8);
        this.offerName = params.get('offerName') || 'RONE-SUB';

        // Restore picks from a shared/bookmarked URL (flavors=variantId-qty_variantId-qty)
        // so opening the same link reproduces the same selection — only known
        // row ids are honored, so a stale/malformed link can't inflate the count.
        // '-' and '_' are used instead of ':' and ',' so the query string never
        // needs percent-encoding (both are unreserved URL characters).
        const validIds = new Set(this.rows.map((row) => row.dataset.variantId));
        this.picks = {};
        const flavorsParam = params.get('flavors');
        if (flavorsParam) {
          flavorsParam.split('_').forEach((pair) => {
            const [variantId, qtyStr] = pair.split('-');
            const qty = parseInt(qtyStr, 10);
            if (variantId && validIds.has(variantId) && qty > 0) {
              this.picks[variantId] = qty;
            }
          });
        }

        if (this.titleEl) this.titleEl.textContent = 'Select at least ' + this.bagWord(this.minBags);
        this.render();
      }

      syncUrl() {
        const params = new URLSearchParams(window.location.search);
        const entries = Object.keys(this.picks).map((id) => id + '-' + this.picks[id]);
        if (entries.length) params.set('flavors', entries.join('_'));
        else params.delete('flavors');
        const url = window.location.pathname + '?' + params.toString();
        window.history.replaceState(window.history.state, '', url);
      }

      count() {
        return Object.values(this.picks).reduce((sum, n) => sum + n, 0);
      }

      bagWord(n) {
        return n + ' bag' + (n === 1 ? '' : 's');
      }

      tierFor(qty) {
        return Math.min(Math.max(qty, 1), 4);
      }

      priceFor(qty) {
        const tier = this.tierFor(qty);
        return this.priceLadder[tier] || this.priceLadder[4];
      }

      // The ongoing renewal rate for this tier — different from priceFor()
      // (today's intro/first-order price) whenever a selling plan's first
      // cycle is discounted below what it renews at. Used specifically for
      // the "refills ship every N days at $X/bag" line, since that's
      // describing future renewals, not the order being placed today.
      renewalPriceFor(qty) {
        const tier = this.tierFor(qty);
        return this.renewalPriceLadder[tier] || this.renewalPriceLadder[4] || this.priceFor(qty);
      }

      cadenceFor(qty) {
        const tier = this.tierFor(qty);
        return this.cadenceLadder[tier] || this.cadenceLadder[4] || 30;
      }

      adjust(variantId, delta) {
        const next = (this.picks[variantId] || 0) + delta;
        if (next <= 0) delete this.picks[variantId];
        else this.picks[variantId] = next;
        this.render();
        // Disabled — stop writing ?flavors= to the URL. Uncomment to restore
        // syncing the picked variant/quantity selection into the query string.
        // this.syncUrl();
      }

      updateGifts(qty) {
        if (!this.giftItems.length) return;
        let unlocked = 0;
        this.giftItems.forEach((item) => {
          const reached = qty >= parseInt(item.dataset.threshold, 10);
          item.classList.toggle('rone-gift--unlocked', reached);
          if (reached) unlocked++;
        });
        if (this.giftCount) this.giftCount.textContent = unlocked;
      }

      render() {
        const n = this.count();
        this.updateGifts(n);

        this.rows.forEach((row) => {
          const variantId = row.dataset.variantId;
          const qty = this.picks[variantId] || 0;
          row.querySelector('[data-flavor-count]').textContent = qty;
          row.classList.toggle('rone-frow--picked', qty > 0);
          row.querySelector('[data-flavor-add]').classList.toggle('rone-frow__add--hide', qty > 0);
          row.querySelector('.rone-frow__step').classList.toggle('rone-frow__step--on', qty > 0);
        });

        if (n === 0) {
          if (this.sumRow) this.sumRow.hidden = true;
          if (this.cadLine) this.cadLine.textContent = '';
        } else {
          const each = this.priceFor(n);
          const total = each * n;
          const msrpTotal = this.msrp * n;
          const pct = msrpTotal > 0 ? Math.round(((msrpTotal - total) / msrpTotal) * 100) : 0;

          if (this.sumRow) this.sumRow.hidden = false;
          if (this.savePct) this.savePct.textContent = pct + '%';
          if (this.bagCount) this.bagCount.textContent = this.bagWord(n) + ' selected· $' + each.toFixed(2) + '/bag';
          if (this.cadLine) {
            const renewalEach = this.renewalPriceFor(n);
            this.cadLine.textContent = 'Subscribe & Save—refills ship every ' + this.cadenceFor(n) + 'days at $' + renewalEach.toFixed(2) + '/bag · adjust or cancel anytime';
          }
        }

        if (n < this.minBags) {
          if (this.cta) this.cta.classList.add('rone-cta--disabled');
          if (this.ctaPriceWrap) this.ctaPriceWrap.hidden = true;
          if (this.ctaLabel) {
            this.ctaLabel.textContent = n === 0
              ? 'Select at least ' + this.bagWord(this.minBags)
              : 'Add ' + (this.minBags - n) + ' more bag' + ((this.minBags - n) === 1 ? '' : 's');
          }
          return;
        }

        const each = this.priceFor(n);
        const total = each * n;
        const msrpTotal = this.msrp * n;

        if (this.cta) this.cta.classList.remove('rone-cta--disabled');
        if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
        if (this.ctaPriceWrap) this.ctaPriceWrap.hidden = false;
        if (this.ctaMsrp) this.ctaMsrp.textContent = '$' + msrpTotal.toFixed(2);
        if (this.ctaTotal) this.ctaTotal.textContent = '$' + total.toFixed(2);
      }

      async addToCart() {
        const n = this.count();
        const tier = this.tierFor(n);
        const sellingPlan = this.sellingPlans[tier] || '';
        await this.clearCart();
        const items = Object.keys(this.picks).map((variantId) => {
          const item = { id: parseInt(variantId, 10), quantity: this.picks[variantId] };
          if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
          return item;
        });

        if (!items.length) return;

        this.ctaLabel && (this.ctaLabel.textContent = 'Adding…');

        fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) throw data.description || 'Could not add to cart';
            if (!this.discountCode) return true;

            // Applies the discount the same way rone-onetime-builder.js
            // applies its own tier discount: a plain GET to Shopify's
            // /discount/:code route sets it on the checkout session via its
            // Set-Cookie response, awaited so the cookie lands before we
            // navigate there. redirect: 'manual' avoids following its 302
            // (whose target can be cross-origin behind a local dev proxy) —
            // the cookie's already set by the first, same-origin response.
            // Resolves to whether it actually worked, not caught away,
            // since the checkout-URL fallback below only kicks in on failure.
            return fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
              method: 'GET',
              credentials: 'same-origin',
              redirect: 'manual',
            })
              .then(() => true)
              .catch((error) => {
                console.warn('[rone-flavor-selector] discount cookie not applied, falling back to checkout URL param:', error);
                return false;
              });
          })
          .then((discountAppliedViaCookie) => {
            // Either the cookie above already set it, or it didn't and this
            // sends the same code through Shopify's own checkout permalink
            // instead — one of the two always gets it to checkout.
            let checkoutUrl = '/checkout';
            if (!discountAppliedViaCookie) {
              checkoutUrl += '?discount=' + encodeURIComponent(this.discountCode);
            }
            window.location.href = checkoutUrl;
            if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
          })
          .catch((error) => {
            console.error(typeof error === 'string' ? error : 'Something went wrong adding your bags to cart.');
            if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
          });
      }
    }
  );
}
